import { ProductPageReviewSummary } from 'models/ProductPageReviewSummary';
import { Action, Dispatch } from 'redux';
import { PageState } from 'models/PageState';
import { isProductPage } from 'models/ProductPageModel';
import {
  postUpVoteReview,
  getProductReviews,
  getProductPageReviewSummary as getProductPageReviewSummaryService
} from 'services/ApiReviewsService';
import { RedReviews } from 'models/RedReviews';
import { emptyFunction } from 'utils';
import { ThunkDispatch } from 'components/Shared/connect';

export interface ProductPageReviewSummarySet extends Action {
  data: ProductPageReviewSummary;
}

export interface ProductPageRedReviewsSet extends Action {
  reviews: RedReviews;
}

const prefix = 'productPage/';
export const PRODUCT_PAGE_REVIEW_SUMMARY_SET = `${prefix}REVIEW_SUMMARY`;
export const PRODUCT_PAGE_UPVOTE_REVIEW = `${prefix}UPVOTE_REVIEW`;
export const PRODUCT_PAGE_RED_REVIEWS_SET = `${prefix}RED_REVIEWS`;

export const productPageRedReviewsSet = (reviews: RedReviews): ProductPageRedReviewsSet => ({
  type: PRODUCT_PAGE_RED_REVIEWS_SET,
  reviews
});

export const productPageReviewSummarySet = (data: ProductPageReviewSummary) => ({
  type: PRODUCT_PAGE_REVIEW_SUMMARY_SET,
  data
});

export const upvoteProductPageReview =
  (reviewId: string) => (dispatch: Dispatch, getState: () => PageState) => {
    const { currentPage } = getState();
    if (!isProductPage(currentPage)) {
      return Promise.resolve();
    }

    return postUpVoteReview(reviewId)
      .then(() => {
        const { redReviews } = currentPage;
        if (redReviews) {
          const updatedReviews = (redReviews?.reviews || []).map(review =>
            review.reviewId === reviewId
              ? { ...review, helpful: review.helpful + 1, hasVoted: true }
              : review
          );

          dispatch(
            productPageRedReviewsSet({
              ...redReviews,
              reviews: updatedReviews
            })
          );
        }

        return;
      })
      .catch(emptyFunction);
  };

export const REVIEWS_PAGE_SIZE = 10;
export const getRedReviews = () => (dispatch: Dispatch, getState: () => PageState) => {
  const { currentPage } = getState();
  if (!isProductPage(currentPage)) {
    return Promise.resolve();
  }

  // When we fetch the reviews, we want to ignore the review
  // that is marked as the most popular review
  const mostPopularReviewId = currentPage.mostPopularReview?.reviewId || '';
  const pageNumber = currentPage.redReviews?.pageNumber || 1;

  return getProductReviews(
    currentPage.product.productId,
    mostPopularReviewId,
    REVIEWS_PAGE_SIZE,
    pageNumber
  )
    .then(({ reviews, pageNumber }) => {
      const oldReviews = currentPage.redReviews?.reviews || [];
      dispatch(
        productPageRedReviewsSet({
          reviews: [...oldReviews, ...reviews],
          pageNumber: pageNumber + 1
        })
      );
    })
    .catch(emptyFunction);
};

export const getProductPageReviewSummary =
  () => (dispatch: ThunkDispatch, getState: () => PageState) => {
    const { currentPage, appShell } = getState();
    if (!isProductPage(currentPage)) {
      return Promise.resolve();
    }

    const isBot = appShell.isBot;

    return getProductPageReviewSummaryService(currentPage.product.productId)
      .then(reviewSummary => {
        if (!isBot && reviewSummary?.review?.productId && reviewSummary?.ratingsPerLevel?.length) {
          dispatch(productPageReviewSummarySet(reviewSummary));
          dispatch(productPageRedReviewsSet({ reviews: [reviewSummary.review], pageNumber: 1 }));
        }
      })
      .catch(emptyFunction);
  };
